// src/contexts/AuthContext.jsx

import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useQueryClient } from '@tanstack/react-query';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true); // Always start in a loading state.
  const [showOnboarding, setShowOnboarding] = useState(false);
  const queryClient = useQueryClient();

  // Effect 1: This effect's ONLY job is to listen to Supabase's auth state.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      // The initial auth check is complete once the first event fires.
      setLoading(false); 
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Effect 2: This effect's ONLY job is to react to changes in the `user` object.
  useEffect(() => {
    if (user && !profile) {
      fetchUserProfile(user);
    } 
    else if (!user) {
      setProfile(null);
      setShowOnboarding(false);
    }
  }, [user, profile]);

  const fetchUserProfile = useCallback(async (currentUser) => {
    if (!currentUser) return;
    try {
      const { data, error } = await supabase.from('profiles').select('*').eq('id', currentUser.id).single();
      if (error && error.code !== 'PGRST116') throw error;
      
      if (data) {
        setProfile(data);
        const needsOnboarding = !data.user_type || !data.ai_interests || data.ai_interests.length === 0;
        setShowOnboarding(needsOnboarding);
      } else {
        setShowOnboarding(true);
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
    }
  }, []);

  const updateProfileAndCompleteOnboarding = async (profileUpdates) => {
    if (!user) return false;
    try {
      const { data, error } = await supabase.from('profiles').update(profileUpdates).eq('id', user.id).select().single();
      if (error) throw error;
      setProfile(data);
      setShowOnboarding(false);
      await queryClient.invalidateQueries({ queryKey: ['userProfile', user.id] });
      await queryClient.invalidateQueries({ queryKey: ['personalizedFeed'] });
      return true;
    } catch (err) {
      console.error('Exception updating profile:', err);
      return false;
    }
  };

  const value = {
    user,
    profile,
    loading,
    showOnboarding,
    fetchUserProfile,
    updateProfileAndCompleteOnboarding,
    signIn: (options) => supabase.auth.signInWithOAuth(options),
    signInWithPassword: (credentials) => supabase.auth.signInWithPassword(credentials),
    signUp: (credentials) => supabase.auth.signUp(credentials),
    signOut: () => supabase.auth.signOut(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};